(function (root) {
  const PENDING_KEY = 'frostix:back-in-stock:pending';
  const SUBSCRIBED_KEY_PREFIX = 'frostix:back-in-stock:subscribed:';

  function buildLoginUrl(loginUrl, returnPath) {
    const separator = loginUrl.includes('?') ? '&' : '?';
    return `${loginUrl}${separator}return_url=${encodeURIComponent(returnPath)}`;
  }

  function subscriptionFromButton(button, returnPath) {
    return {
      variantId: button.dataset.variantId || '',
      productId: button.dataset.productId || '',
      productTitle: button.dataset.productTitle || '',
      productUrl: button.dataset.productUrl || '',
      returnPath,
    };
  }

  function createSubscriptionRequest(email, variantId) {
    return {
      url: 'https://restock.frustix.com/v1/back-in-stock',
      options: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, variantId }),
      },
    };
  }

  function markVariantSubscribed(scope, variantId, label) {
    scope.querySelectorAll('[data-back-in-stock]').forEach((button) => {
      if (button.dataset.variantId !== String(variantId)) return;
      button.classList.add('is-subscribed');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      const labelNode = button.querySelector('[data-bis-label]');
      if (labelNode) labelNode.textContent = label;
    });
  }

  function subscriptionKey(variantId) {
    return `${SUBSCRIBED_KEY_PREFIX}${variantId}`;
  }

  function rememberVariantSubscription(storages, variantId) {
    const storageList = Array.isArray(storages) ? storages : [storages];
    const key = subscriptionKey(variantId);
    for (const storage of storageList) {
      try {
        storage?.setItem(key, '1');
        if (storage?.getItem(key) === '1') return true;
      } catch (_error) {
        // Try the next browser storage backend.
      }
    }
    return false;
  }

  function hasVariantSubscription(persistentStorage, legacyStorage, variantId) {
    const key = subscriptionKey(variantId);
    try {
      if (persistentStorage?.getItem(key) === '1') return true;
    } catch (_error) {
      // Fall back to the legacy session marker when persistent storage is blocked.
    }

    let hasLegacyMarker = false;
    try {
      hasLegacyMarker = legacyStorage?.getItem(key) === '1';
    } catch (_error) {
      return false;
    }
    if (!hasLegacyMarker) return false;

    try {
      if (persistentStorage) {
        persistentStorage.setItem(key, '1');
        if (persistentStorage.getItem(key) === '1') legacyStorage.removeItem(key);
      }
    } catch (_error) {
      // Keep the working session marker when migration is blocked.
    }
    return true;
  }

  function reconcileVariantAvailability(storages, variantId, available) {
    if (!available || !variantId) return;
    const storageList = Array.isArray(storages) ? storages : [storages];
    storageList.forEach((storage) => {
      try {
        storage?.removeItem(subscriptionKey(variantId));
      } catch (_error) {
        // The available product remains purchasable even if storage is blocked.
      }
    });
  }

  function hasRenderedRestockButton(scope, variantId) {
    if (!scope || !variantId) return false;
    const buttons = [];
    if (scope.matches?.('[data-back-in-stock]')) buttons.push(scope);
    scope.querySelectorAll?.('[data-back-in-stock]').forEach((button) => buttons.push(button));
    return buttons.some((button) => button.dataset.variantId === String(variantId));
  }

  function reconcileRenderedVariantAvailability(storages, scope, variantId, available) {
    if (hasRenderedRestockButton(scope, variantId)) return;
    reconcileVariantAvailability(storages, variantId, available);
  }

  function availableVariantIdFromForm(form) {
    const input = form?.querySelector?.('input[name="id"]');
    if (!input || input.disabled || !input.value) return '';

    const variantId = String(input.value);
    return hasRenderedRestockButton(form, variantId) ? '' : variantId;
  }

  async function klaviyoErrorFromResponse(response) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (_error) {
      // Some network/proxy failures return an empty or non-JSON response.
    }

    const apiError = payload?.errors?.[0];
    const detail =
      payload?.detail ||
      (typeof apiError === 'string'
        ? apiError
        : apiError?.detail || payload?.message || `Back in Stock returned ${response.status}`);
    const error = new Error(detail);
    error.status = response.status;
    error.code = payload?.code || (typeof apiError === 'object' && apiError ? apiError.code || '' : '');
    return error;
  }

  const api = {
    buildLoginUrl,
    subscriptionFromButton,
    createSubscriptionRequest,
    markVariantSubscribed,
    rememberVariantSubscription,
    hasVariantSubscription,
    reconcileVariantAvailability,
    reconcileRenderedVariantAvailability,
    availableVariantIdFromForm,
    klaviyoErrorFromResponse,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (!root || !root.document || !root.HTMLElement || !root.customElements) return;

  function browserStorage(name) {
    try {
      return root[name];
    } catch (_error) {
      return null;
    }
  }

  const pendingStorage = browserStorage('sessionStorage');
  const subscribedStorage = browserStorage('localStorage');

  function readJson(key) {
    try {
      const raw = pendingStorage?.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  function writeJson(key, value) {
    try {
      pendingStorage?.setItem(key, JSON.stringify(value));
    } catch (_error) {
      // The login flow remains non-fatal when browser storage is blocked.
    }
  }

  function removeStored(key) {
    try {
      pendingStorage?.removeItem(key);
    } catch (_error) {
      // No-op.
    }
  }

  function currentReturnPath() {
    return `${root.location.pathname}${root.location.search}${root.location.hash}`;
  }

  function isSubscribed(variantId) {
    return hasVariantSubscription(subscribedStorage, pendingStorage, variantId);
  }

  function rememberSubscribed(variantId) {
    rememberVariantSubscription([subscribedStorage, pendingStorage], variantId);
  }

  class BackInStockModal extends root.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.customerEmail = this.dataset.customerEmail || '';
      this.loginUrl = this.dataset.loginUrl || '/account/login';
      this.subscribedLabel = this.dataset.subscribedLabel || '';
      this.errorText = this.querySelector('[data-bis-error-text]');
      this.defaultErrorText = this.errorText?.textContent || '';
      this.activeContext = null;
      this.previousFocus = null;

      this.querySelectorAll('[data-bis-close]').forEach((element) => {
        element.addEventListener('click', () => this.close());
      });
      this.querySelector('[data-bis-login]')?.addEventListener('click', () => this.goToLogin());
      this.querySelector('[data-bis-retry]')?.addEventListener('click', () => {
        if (this.activeContext) this.subscribe(this.activeContext);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('variant:change', (event) => {
        const variant = event.detail?.variant;
        reconcileRenderedVariantAvailability(
          [subscribedStorage, pendingStorage],
          document,
          variant?.id,
          variant?.available === true
        );
        root.setTimeout(() => this.restoreButtonStates(document), 0);
      });

      this.restoreButtonStates(document);
      root.setTimeout(() => this.resumeAfterLogin(), 0);
    }

    restoreButtonStates(scope) {
      const productFormSelector = 'form[action*="/cart/add"], form[data-type="add-to-cart-form"]';
      const productForms = [];
      if (scope.matches?.(productFormSelector)) productForms.push(scope);
      scope.querySelectorAll?.(productFormSelector).forEach((form) => productForms.push(form));
      productForms.forEach((form) => {
        const variantId = availableVariantIdFromForm(form);
        reconcileRenderedVariantAvailability([subscribedStorage, pendingStorage], document, variantId, Boolean(variantId));
      });

      const availableButtons = [];
      const availableButtonSelector = 'button[data-variant-id]:not([data-back-in-stock]):not([disabled])';
      if (scope.matches?.(availableButtonSelector)) availableButtons.push(scope);
      scope.querySelectorAll?.(availableButtonSelector).forEach((button) => {
        availableButtons.push(button);
      });
      availableButtons.forEach((button) => {
        if (button.dataset.variantId) {
          reconcileRenderedVariantAvailability(
            [subscribedStorage, pendingStorage],
            document,
            button.dataset.variantId,
            true
          );
        }
      });

      scope.querySelectorAll?.('script[data-selected-variant]').forEach((script) => {
        try {
          const variant = JSON.parse(script.textContent);
          reconcileRenderedVariantAvailability(
            [subscribedStorage, pendingStorage],
            document,
            variant?.id,
            variant?.available === true
          );
        } catch (_error) {
          // Invalid product JSON is handled by the existing variant selector.
        }
      });

      const buttons = [];
      if (scope.matches?.('[data-back-in-stock]')) buttons.push(scope);
      scope.querySelectorAll?.('[data-back-in-stock]').forEach((button) => buttons.push(button));
      buttons.forEach((button) => {
        if (button.dataset.variantId && isSubscribed(button.dataset.variantId)) {
          markVariantSubscribed(document, button.dataset.variantId, this.subscribedLabel);
        }
      });
    }

    showStep(stepName) {
      this.querySelectorAll('[data-bis-step]').forEach((step) => {
        step.hidden = step.dataset.bisStep !== stepName;
      });
    }

    open(stepName) {
      if (!this.hasAttribute('open')) this.previousFocus = document.activeElement;
      this.showStep(stepName);
      this.setAttribute('open', '');
      document.documentElement.classList.add('back-in-stock-modal-open');
      this.querySelector('[data-bis-close-button]')?.focus();
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('back-in-stock-modal-open');
      this.classList.remove('is-loading');
      if (this.previousFocus?.focus) this.previousFocus.focus();
    }

    handleButton(button) {
      const context = subscriptionFromButton(button, currentReturnPath());
      if (!context.variantId) return;
      this.activeContext = context;

      if (isSubscribed(context.variantId)) {
        markVariantSubscribed(document, context.variantId, this.subscribedLabel);
        this.open('success');
        return;
      }

      if (!this.customerEmail) {
        this.open('guest');
        return;
      }

      this.subscribe(context);
    }

    goToLogin() {
      if (!this.activeContext) return;
      writeJson(PENDING_KEY, this.activeContext);
      root.location.assign(buildLoginUrl(this.loginUrl, this.activeContext.returnPath || '/'));
    }

    async resumeAfterLogin() {
      if (!this.customerEmail) return;
      const pending = readJson(PENDING_KEY);
      if (!pending?.variantId) return;
      this.activeContext = pending;
      await this.subscribe(pending);
    }

    async subscribe(context) {
      this.classList.add('is-loading');
      if (this.errorText) this.errorText.textContent = this.defaultErrorText;
      this.open('loading');

      try {
        const request = createSubscriptionRequest(this.customerEmail, context.variantId);
        const response = await root.fetch(request.url, request.options);

        if (!response.ok) throw await klaviyoErrorFromResponse(response);

        rememberSubscribed(context.variantId);
        removeStored(PENDING_KEY);
        markVariantSubscribed(document, context.variantId, this.subscribedLabel);
        this.classList.remove('is-loading');
        this.open('success');
      } catch (error) {
        this.classList.remove('is-loading');
        const isDebug = new URLSearchParams(root.location.search).has('bis_debug');
        if (isDebug && this.errorText) {
          const errorCode = [error.status, error.code].filter(Boolean).join(' / ');
          this.errorText.textContent = `${this.defaultErrorText} ${errorCode ? `[${errorCode}] ` : ''}${error.message}`;
        }
        this.open('error');
        console.error('Back in Stock subscription failed', {
          status: error.status || 0,
          code: error.code || '',
          detail: error.message,
          variantId: context.variantId,
        });
      }
    }
  }

  if (!root.customElements.get('back-in-stock-modal')) {
    root.customElements.define('back-in-stock-modal', BackInStockModal);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-back-in-stock]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    document.getElementById('BackInStockModal')?.handleButton(button);
  });

  const refreshButtons = (scope) => {
    document.getElementById('BackInStockModal')?.restoreButtonStates(scope);
  };

  document.addEventListener('DOMContentLoaded', () => {
    refreshButtons(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) refreshButtons(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})(typeof window !== 'undefined' ? window : undefined);
